// Stems export dialog: pick up to 3 tracks + WAV/MP3 format, bundled to a zip.
// Owns its own open/format/selection state; Toolbar only renders the trigger.
import React, { useState, forwardRef, useImperativeHandle } from "react";
import { Export } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

export const StemsDialog = forwardRef(function StemsDialog({ project, tracks, exporting, onExportStems }, ref) {
  const [open, setOpen] = useState(false);
  const [format, setFormat] = useState("wav");
  const [sel, setSel] = useState([]);
  const stemTracks = tracks.filter((t) => t.buffer);

  useImperativeHandle(ref, () => ({ open: () => setOpen(true) }));

  function toggleStem(id) {
    setSel((s) => {
      if (s.includes(id)) return s.filter((x) => x !== id);
      if (s.length >= 3) return s;
      return [...s, id];
    });
  }

  async function runExport() {
    const ok = await onExportStems(sel, format);
    if (ok) {
      setOpen(false);
      setSel([]);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setSel([]); }}>
      <DialogContent className="bg-[#1A1B1D] border border-[#34373B] rounded-3xl text-white" data-testid="stems-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl tracking-tighter flex items-center gap-2">
            <Export size={22} weight="bold" /> Export stems
          </DialogTitle>
          <DialogDescription className="text-[#9CA0A6] text-sm">
            Pick up to 3 tracks — each renders as its own file at the current pattern and {project?.bpm} BPM, bundled in one zip.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-1">
          <div className="flex items-center gap-2">
            <span className="text-[#9CA0A6] uppercase text-xs tracking-widest font-mono mr-1">Format</span>
            {["wav", "mp3"].map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFormat(f)}
                data-testid={`stems-format-${f}`}
                className={`bb-pill h-9 px-4 text-xs font-mono uppercase tracking-widest border transition-colors ${
                  format === f
                    ? "bg-[#A8FF1F] border-[#A8FF1F] text-[#0A0A0A] font-semibold"
                    : "bg-[#232427] border-[#34373B] text-[#9CA0A6] hover:text-white"
                }`}
              >
                {f === "wav" ? "WAV · Lossless" : "MP3 · 192 kbps"}
              </button>
            ))}
          </div>
          <div className="max-h-60 overflow-y-auto space-y-1.5 pr-1" data-testid="stems-track-list">
            {stemTracks.map((t) => {
              const checked = sel.includes(t.id);
              const locked = !checked && sel.length >= 3;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => toggleStem(t.id)}
                  disabled={locked}
                  data-testid={`stems-track-${t.id}`}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-2xl border text-left transition-colors ${
                    checked
                      ? "bg-[#A8FF1F]/10 border-[#A8FF1F]"
                      : "bg-[#232427] border-[#34373B] hover:border-[#5A5E63]"
                  } ${locked ? "opacity-40 cursor-not-allowed" : ""}`}
                >
                  <span
                    className={`shrink-0 w-4 h-4 rounded grid place-items-center border ${
                      checked ? "bg-[#A8FF1F] border-[#A8FF1F] text-[#0A0A0A]" : "border-[#5A5E63]"
                    }`}
                  >
                    {checked && <span className="text-[10px] font-bold leading-none">✓</span>}
                  </span>
                  <span className="flex-1 truncate font-mono text-sm">{t.name}</span>
                  <span className="shrink-0 text-[10px] font-mono uppercase tracking-widest text-[#9CA0A6]">
                    {t.kind}{t.is_synced ? " · synced" : ""}
                  </span>
                </button>
              );
            })}
            {stemTracks.length === 0 && (
              <p className="text-[#9CA0A6] text-sm font-mono py-4 text-center">No loaded tracks to export yet.</p>
            )}
          </div>
        </div>
        <DialogFooter className="mt-2 flex items-center gap-3 sm:justify-between">
          <span className="text-[#9CA0A6] text-xs font-mono uppercase tracking-widest" data-testid="stems-count">
            {sel.length}/3 selected
          </span>
          <Button
            onClick={runExport}
            disabled={exporting || sel.length === 0}
            data-testid="stems-export-btn"
            className="bb-pill bg-[#A8FF1F] hover:bg-[#94E600] text-[#0A0A0A] h-11 px-6 font-semibold disabled:opacity-40"
          >
            {exporting ? "Rendering…" : `Export ${sel.length || ""} stem${sel.length === 1 ? "" : "s"} (ZIP)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});
